import ChatAttachmentChips from "../ChatAttachmentChips";
import type { ChatMessage } from "@lib/agent/types";
import { Group, Paper, Stack, Text, useMantineColorScheme } from "@mantine/core";

// System-event messages are persisted as user-role with a `[SYSTEM] …` prefix (see
// `apps/agent_proxy/service.py:agent_session_inject_system_message`). The text-prefix
// discriminator keeps the backend schema simple at the cost of stringly-typed detection
// here; a dedicated chat role would be cleaner if we add a second event type.
const SYSTEM_MESSAGE_PREFIX = "[SYSTEM] ";

function isSystemMessage(message: ChatMessage): boolean {
  return message.role === "user" && message.content.startsWith(SYSTEM_MESSAGE_PREFIX);
}

export default function UserChatMessage({ message }: { message: ChatMessage }) {
  const attachments = message.attachments ?? [];
  const { colorScheme } = useMantineColorScheme();

  if (isSystemMessage(message)) {
    return (
      <Group justify="center" w="100%" py={2}>
        <Text size="xs" c="dimmed" fs="italic" ta="center" style={{ textWrap: "balance" }}>
          {message.content.slice(SYSTEM_MESSAGE_PREFIX.length)}
        </Text>
      </Group>
    );
  }

  const isUser = message.role === "user";

  return (
    <Stack flex={1} w="100%" align="flex-end">
      {attachments.length > 0 && <ChatAttachmentChips fileNames={attachments.map(a => a.filename)} />}
      <Paper
        py={5}
        px="sm"
        radius="lg"
        bg={
          isUser
            ? colorScheme === "dark"
              ? "var(--mantine-color-default-border)"
              : "var(--mantine-color-gray-light-hover)"
            : "transparent"
        }
        shadow={isUser ? "xxs" : "none"}
        withBorder={false}
        maw="90%"
        mb="md"
      >
        <Stack gap={4}>
          {message.content && (
            <Text size="sm" fw={500} c="var(--mantine-color-bright)">
              {message.content}
            </Text>
          )}
        </Stack>
      </Paper>
    </Stack>
  );
}
